/**
 * Central place for internal app routes + URL builders.
 *
 * Notes:
 * - Prefer using these helpers instead of hardcoding strings in `Link`, `router.push`, `redirect`, etc.
 */
import type { Building, Owner } from '@repo/api';

export const v4Link = 'https://v4.openigloo.com';

export const ROUTES = {
  home: '/',
  listings: '/listings',
  building: '/building',
  contact: '/contact',
  blogs: `/blogs`,
} as const;

/**
 * Listings filters are often encoded into a single path segment, e.g.:
 * - `status:verified`
 * - `borough:queens|nbr:astoria`
 */
export function buildListingsFilterPayload(
  parts: Array<[key: string, value: string | number | null | undefined]>,
): string {
  return parts
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}:${String(v)}`)
    .join('|');
}

export function listingsUrl(filterPayload?: string): string {
  return filterPayload ? `${ROUTES.listings}/${filterPayload}` : ROUTES.listings;
}

export type ListingStatus =
  | 'verified'
  | 'top-rated'
  | 'rent-stabilized'
  | 'good-cause'
  | (string & {});

export function listingsStatusUrl(status: ListingStatus): string {
  return listingsUrl(buildListingsFilterPayload([['status', status]]));
}

/**
 * Normalize a `city` display value ("New York", "Boston") to the URL slug the
 * app's routes expect ("nyc", "boston"). Used for neighborhood cities (similar
 * listings, unit canonicals) and contact cities (`contactUrl`). Missing or
 * unknown values fall back to "nyc".
 */
export function cityIdFromNeighborhood(cityValue: string | null | undefined): string {
  const normalized = (cityValue ?? '').trim().toLowerCase();
  if (!normalized) return 'nyc';
  if (normalized === 'new york' || normalized === 'nyc') return 'nyc';
  if (normalized === 'boston' || normalized === 'cambridge') return 'boston';
  return 'nyc';
}

/**
 * Borough slug used by the `/buildings` hubs: "Manhattan" → "manhattan",
 * "The Bronx" → "bronx", "Staten Island" → "staten-island". Same convention
 * as scripts/generate-nbhd-borough-map.ts. Note the listings filters use
 * "the-bronx" instead.
 */
export function buildingsBoroughSlug(borough: string): string {
  return borough
    .toLowerCase()
    .replace(/^the\s+/, '')
    .trim()
    .replace(/\s+/g, '-');
}

/** `/buildings/{borough}` or `/buildings/{borough}/{neighborhood}`. */
export function buildingsHubUrl(borough: string, neighborhoodSlug?: string | null): string {
  const base = `/buildings/${buildingsBoroughSlug(borough)}`;
  return neighborhoodSlug ? `${base}/${neighborhoodSlug}` : base;
}

/**
 * Path for a building.
 *
 * The city fallback maps the neighborhood's DISPLAY name to a city id. That
 * field holds "New York", not "nyc", so lowercasing it alone produced
 * "/building/new%20york/..." whenever `primaryAddress` was absent — one half of
 * the malformed canonical those pages emitted. `contactUrl` below has always
 * done this mapping; this did not.
 *
 * NOTE: still interpolates `primaryAddress?.slug` unguarded, so a building with
 * no address yields ".../undefined". Kept deliberately — 21 call sites treat the
 * return as a plain string, and narrowing it to `string | null` here is a wider
 * change than the defect warrants. Callers that put this in a canonical tag or a
 * sitemap must gate on `hasCanonicalBuildingUrl()` first.
 */
export function buildingUrl(building: Building) {
  const neighborhoodCity = building.neighborhood?.city?.toLowerCase();
  const city =
    building.primaryAddress?.cityId ||
    (neighborhoodCity === 'new york' ? 'nyc' : neighborhoodCity) ||
    'nyc';
  return `${ROUTES.building}/${city}/${building.slug}/${building.primaryAddress?.slug}`;
}

/**
 * Whether `buildingUrl()` yields a routable URL for this building.
 *
 * False when building-svc has no primary address — a real state, since a
 * building qualifies for a page on reviews or violations alone. Gate canonical
 * tags, `permanentRedirect()` targets and structured-data URLs on this; a false
 * here means the page has no canonical URL yet and should not be indexed.
 */
export function hasCanonicalBuildingUrl(building: Building): boolean {
  return Boolean(building.primaryAddress?.slug);
}

/**
 * Path for a contact.
 *
 * Contacts with no `city` (common for owners imported from public records)
 * used to produce "/contact/undefined/...", a duplicate of the "/contact/nyc/..."
 * page. The city now goes through `cityIdFromNeighborhood`, which falls back to
 * "nyc". This is also the canonical URL of a contact page.
 */
export function contactUrl(owner: Pick<Owner, 'id' | 'slug' | 'city'>) {
  return `${ROUTES.contact}/${cityIdFromNeighborhood(owner.city)}/${owner.id}/${owner.slug}`;
}
